import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdChevronLeft,
  MdOutlineSecurity,
  MdOutlineLogout,
  MdCheck
} from 'react-icons/md';

import profileIcon from '../../images/profile.png';
import discoverIcon from '../../images/discover.png';
import messagesIcon from '../../images/messages.png';
import accountIcon from '../../images/account.png';
import premiumIcon from '../../images/premium.png';
import stealthIcon from '../../images/stealth-hide.png';

import './HowToUse.css';

const ChevronIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#eedcc5"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="how-to-use-chevron-svg"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const GuideCard = ({ icon, imageSrc, title, subtitle, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`how-to-use-card ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className="how-to-use-card-header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="how-to-use-icon-badge">
          {imageSrc ? (
            <img src={imageSrc} alt="" className="how-to-use-icon-img" />
          ) : (
            icon
          )}
        </div>
        <div className="how-to-use-header-text">
          <span className="how-to-use-card-title">{title}</span>
          {subtitle && <span className="how-to-use-card-subtitle">{subtitle}</span>}
        </div>
        <div className={`how-to-use-chevron-wrap ${isOpen ? 'open' : ''}`}>
          <ChevronIcon />
        </div>
      </button>

      {isOpen && (
        <div className="how-to-use-card-body">
          <div className="how-to-use-card-divider" />
          <div className="how-to-use-content-inner">{children}</div>
        </div>
      )}
    </div>
  );
};

export const HowToUse = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const sc = document.getElementById('scroll-container');
    if (sc) {
      sc.style.backgroundColor = '#0d0e11';
      sc.style.paddingBottom = '0px';
    }
    return () => {
      if (sc) {
        sc.style.backgroundColor = '';
        sc.style.paddingBottom = '';
      }
    };
  }, []);

  return (
    <div className="main settings how-to-use-page">
      <div className="how-to-use-content">
        {/* Header with Back Button */}
        <div className="how-to-use-header">
          <div className="how-to-use-title-row">
            <button
              type="button"
              className="how-to-use-back-btn"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/settings');
                }
              }}
              aria-label="Back"
            >
              <MdChevronLeft className="how-to-use-back-icon" />
            </button>
            <h1 className="how-to-use-page-title">How to use</h1>
          </div>
          <p className="how-to-use-page-subtitle">Learn how to get the most out of the app</p>
        </div>

        {/* Guide Cards List */}
        <div className="how-to-use-cards-list">
          {/* 1. Profile */}
          <GuideCard
            imageSrc={profileIcon}
            title="Profile"
            subtitle="Showcase yourself, edit bio, and manage photos"
          >
            <p className="how-to-use-p">
              This page displays your personal profile as other users see it. You can manage your
              public information, customize your bio, and update your photos anytime.
            </p>
            <div className="how-to-use-features-block">
              <span className="how-to-use-features-title">Key Features:</span>
              <ul className="how-to-use-feature-list">
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Preview your profile as it appears to others.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Edit your personal details, interests, role, and bio.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Upload and organize your profile photos.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Manually set your location to connect with users in specific regions.</span>
                </li>
              </ul>
            </div>
          </GuideCard>

          {/* 2. Discover */}
          <GuideCard
            imageSrc={discoverIcon}
            title="Discover"
            subtitle="Explore profiles, filter by criteria, and find people nearby"
          >
            <p className="how-to-use-p">
              Discover is where you browse and connect with users on the platform. Customize your
              search with free and premium filters to find exact matches.
            </p>
            <div className="how-to-use-features-block">
              <span className="how-to-use-features-title">Filters & Discovery:</span>
              <ul className="how-to-use-feature-list">
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Filter by Age range and Proximity distance.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>
                    <strong>Premium Filters:</strong> Role (Top, Bottom, Versatile), meeting preference,
                    intentions, and online status.
                  </span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Browse nearby active profiles in real time.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Track your interactions including Views, Likes, and Favorites.</span>
                </li>
              </ul>
            </div>
          </GuideCard>

          {/* 3. Messages */}
          <GuideCard
            imageSrc={messagesIcon}
            title="Messages"
            subtitle="Private conversations, photo sharing, and instant alerts"
          >
            <p className="how-to-use-p">
              Your inbox holds all your private chats. Read and reply to messages in real time with
              complete control over your conversations.
            </p>
            <div className="how-to-use-features-block">
              <span className="how-to-use-features-title">Messaging Features:</span>
              <ul className="how-to-use-feature-list">
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Instant real-time messaging with online status indicators.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Send and receive private images securely in chat.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>
                    <strong>Premium Perk:</strong> Delete sent and received conversations for both parties.
                  </span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Receive instant push and in-app notifications for incoming chats.</span>
                </li>
              </ul>
            </div>
          </GuideCard>

          {/* 4. Account & Settings */}
          <GuideCard
            imageSrc={accountIcon}
            title="Account & Settings"
            subtitle="Manage preferences, subscription, notifications, and security"
          >
            <div className="how-to-use-subsections">
              <div className="how-to-use-subsection">
                <span className="how-to-use-sub-title">Account Settings</span>
                <p className="how-to-use-sub-desc">
                  Update your email address, change password, configure location, toggle Invisible
                  Mode (Premium), or delete your account.
                </p>
              </div>
              <div className="how-to-use-subsection">
                <span className="how-to-use-sub-title">Manage Subscription</span>
                <p className="how-to-use-sub-desc">
                  View your current Free or Premium subscription status, upgrade to Premium, or manage
                  your active billing plan.
                </p>
              </div>
              <div className="how-to-use-subsection">
                <span className="how-to-use-sub-title">Notification Settings</span>
                <p className="how-to-use-sub-desc">
                  Control email and push alerts. Premium members can customize email titles and
                  preview message contents directly in alerts.
                </p>
              </div>
              <div className="how-to-use-subsection">
                <span className="how-to-use-sub-title">Blocked Users</span>
                <p className="how-to-use-sub-desc">
                  Review accounts you have blocked, unblock anytime, or submit reports regarding
                  inappropriate behavior.
                </p>
              </div>
            </div>
          </GuideCard>

          {/* 5. Try Premium */}
          <GuideCard
            imageSrc={premiumIcon}
            title="Try Premium"
            subtitle="Unlock elevated privacy, unlimited filters, and exclusive perks"
          >
            <p className="how-to-use-p">
              Upgrade to Premium for an enhanced, completely unrestricted experience across the platform.
            </p>
            <div className="how-to-use-features-block">
              <span className="how-to-use-features-title">Premium Highlights:</span>
              <ul className="how-to-use-feature-list">
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Flexible Weekly, Monthly, and Yearly subscription options.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Unlock all advanced Discovery filters (Role, Intentions, Online).</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Enable Invisible Mode to browse without appearing in others' view lists.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Full control to delete entire chat threads for both sides.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Custom email alert subjects and message preview toggles.</span>
                </li>
              </ul>
            </div>
          </GuideCard>

          {/* 6. Stealth Mode */}
          <GuideCard
            imageSrc={stealthIcon}
            title="Enable Stealth"
            subtitle="Browse with maximum discretion in public spaces"
          >
            <p className="how-to-use-p">
              Stealth Mode conceals profile images across the app to ensure your browsing remains private
              even in crowded or shared environments.
            </p>
            <div className="how-to-use-features-block">
              <span className="how-to-use-features-title">How It Works:</span>
              <ul className="how-to-use-feature-list">
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>All profile photos are masked and hidden across feeds.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Easily toggle on and off anytime via the eye icon in the bottom navigation.</span>
                </li>
                <li>
                  <MdCheck className="how-to-use-check-icon" />
                  <span>Maintains your privacy wherever you are.</span>
                </li>
              </ul>
            </div>
          </GuideCard>

          {/* 7. Safety, Blocking & Reporting */}
          <GuideCard
            icon={<MdOutlineSecurity className="how-to-use-icon-svg" />}
            title="Blocking & Reporting"
            subtitle="Keep your space safe and report community guideline violations"
          >
            <p className="how-to-use-p">
              Your safety and privacy are our top priorities. You can block or report any member at any time.
            </p>
            <div className="how-to-use-steps-block">
              <span className="how-to-use-features-title">How to Block a User:</span>
              <ol className="how-to-use-steps-list">
                <li>Go to the profile of the user you wish to block.</li>
                <li>Tap the <strong>"Block / Report"</strong> button.</li>
                <li>Confirm your choice; the user is immediately added to your blocked list.</li>
              </ol>

              <span className="how-to-use-features-title" style={{ marginTop: '16px', display: 'block' }}>
                How to Report a User:
              </span>
              <ol className="how-to-use-steps-list">
                <li>First, block the user to restrict further interaction.</li>
                <li>Open <strong>Settings &gt; Blocked Users</strong>.</li>
                <li>Find their name in your blocked list and tap <strong>"Report"</strong>.</li>
                <li>Select a reason, provide any details, and submit for our moderation team to review.</li>
              </ol>
            </div>
          </GuideCard>

          {/* 8. Signing Out & Deleting Account */}
          <GuideCard
            icon={<MdOutlineLogout className="how-to-use-icon-svg" />}
            title="Sign Out & Account Deletion"
            subtitle="How to sign out securely or permanently delete your profile"
          >
            <div className="how-to-use-steps-block">
              <span className="how-to-use-features-title">Signing Out:</span>
              <p className="how-to-use-sub-desc" style={{ marginBottom: '16px' }}>
                Open the Settings menu by tapping the gear icon in the navigation bar, scroll down, and tap{' '}
                <strong>"Sign Out"</strong>.
              </p>

              <span className="how-to-use-features-title">Deleting Your Account:</span>
              <ol className="how-to-use-steps-list">
                <li>Navigate to <strong>Account Settings</strong>.</li>
                <li>Scroll down to the <strong>"Delete Account"</strong> section.</li>
                <li>Tap <strong>"Yes, I want to permanently delete my account"</strong>.</li>
                <li>Confirm by entering your password in the confirmation modal.</li>
              </ol>
              <div className="how-to-use-note-callout">
                <strong>Note:</strong> If you have an active Premium subscription, please cancel it through
                Manage Subscription before deleting your account.
              </div>
            </div>
          </GuideCard>
        </div>
      </div>
    </div>
  );
};

export default HowToUse;
